import {WBPopupTrigger} from '@wandb/ui';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {makeDidPropsOrStateChange} from '@wandb/weave/common/util/shouldUpdate';
import {Button} from '@wandb/weave/components/Button';
import {RunState} from '@wandb/weave/components/RunState';
import {WorkspaceTag} from '@wandb/weave/components/Tag/WorkspaceTag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import produce from 'immer';
import * as _ from 'lodash';
import memoize from 'memoize-one';
import React from 'react';
import {Link, Redirect} from 'react-router-dom';

import BulkTagEditor, {TagState} from '../../components/Tags/BulkTagEditor';
import {combinedFilter} from '../../state/graphql/runSelectorQuery';
import {setCreateSweepArgs} from '../../state/sweeps/actions';
import {BenchmarkProject} from '../../types/graphql';
import * as FilterTypes from '../../util/filterTypes';
import * as Query from '../../util/queryts';
import * as QueryTypes from '../../util/queryTypes';
import {Config as TableSettings} from '../../util/runfeed';
import * as Run from '../../util/runs';
import * as RunTypes from '../../util/runTypes';
import {getSweepDisplayName} from '../../util/sweeps';
import * as TableCols from '../../util/tablecols';
import {WBTableRowWithRunSetInfo} from '../../util/tablecols';
import * as urls from '../../util/urls';
import {CreateSweepButton} from '../CreateSweepButton';
import {DeleteRunsButton} from '../DeleteRunsButton';
import {EditableLabel} from '../elements/EditableLabel';
import {TimeDisplay} from '../elements/TimeDisplay';
import {RunTagInfiniteDropdown} from '../InfiniteScrollDropdown/RunTagComponents';
import {JobDrawerLocked} from '../Launch/JobDrawer/JobDrawer';
import {ProjectPicker} from '../ProjectPicker/ProjectPicker';
import {RunMover} from '../RunMover';
import {RunsSearchBar} from '../RunsSearchBar';
import {
  type RunSelectorPropsHook,
  useRunSelectorProps,
} from './hooks/useRunSelectorProps';
import {MoveRunDrawerBulkButton} from './MoveRunDrawer/MoveRunDrawerBulk';
import {RunsAutoCols} from './RunsAutoCols';
import {RunsColumnEditor} from './RunsColumnEditor';
import {RunsDownloadMenu} from './RunsDownloadMenu';
import * as S from './RunSelectorInner.styles';
import {RunSelectorRowHeader} from './RunSelectorRowHeader';
import {RunsFreezeAndFilter} from './RunsFreeze';
import {RunsGroupTableAction} from './RunsGrouping';
import {RunsSortTableAction} from './RunsSort';
import {makeRunsSortIndicator} from './RunsSortIndicator';
import {RunsTableHeader} from './RunsTableHeader';
import type {DisabledFeaturesMap, RunSelectorInnerProps} from './types';
import type {WBTableColumn, WBTableProps} from './WBTable/types';
import {WBTable, WBTableComp} from './WBTable/WBTable';
import {WBTableCellCreatedAt} from './WBTable/WBTableCellCreatedAt';
import {WBTableCellDefault} from './WBTable/WBTableCellDefault';

type RenderRowHeaderOpts = {
  benchmarkMeta?: BenchmarkProject;
  disableRunLinks: boolean;
  displayBenchmark: boolean;
  isHoveringRow: boolean;
  entityName: string;
  isFirstActiveRun: boolean;
  isGroup: boolean;
  isGroupExpanded: boolean;
  isLoadingChildren: boolean;
  projectName: string;
  recursionDepth: number;
  row: WBTableRowWithRunSetInfo;
  toggleExpandedRow?: (rowAddress: string) => void;
};

type AddFilterFn = (
  key: RunTypes.Key,
  op: FilterTypes.ValueOp,
  value: RunTypes.Value
) => void;

type RenderCellOpts = {
  addFilter?: AddFilterFn;
  benchmarkMeta?: BenchmarkProject;
  column: WBTableColumn;
  disabledFeatures: DisabledFeaturesMap;
  disableRunLinks: boolean;
  displayBenchmark: boolean;
  entityName: string;
  firstActiveRunName: string | undefined;
  isGroup: boolean;
  isGroupExpanded: boolean;
  isHoveringRow: boolean;
  loadingChildren: boolean;
  projectName: string;
  recursionDepth: number;
  row: WBTableRowWithRunSetInfo;
  toggleExpandedRow?: (rowAddress: string) => void;
};

type RenderTagsCellOpts = {
  disabledFeatures: DisabledFeaturesMap;
  entityName: string;
  isGroup: boolean;
  isGroupRun: boolean;
  isHoveringRow: boolean;
  onAddFilter?: AddFilterFn;
  onAddTag: (tagName: string) => void;
  onDeleteTag: (tagName: string) => void;
  projectName: string;
  row: WBTableRowWithRunSetInfo;
};

type RenderHeaderOpts = {
  displayedRows: RunTypes.Run[];
  tableSettings: TableSettings;
  totalRowCount: number;
};

/**
 * RunSelectorInner is WBTable, wrapped with GQL queries for project info,
 * tags, and runs. For runs data, there's a top-level query to populate the
 * table and separate sub-queries for each expanded group (see WBTableRow)
 * and CSV download (see TableExport).
 */

const FIXED_COLUMNS = ['run:displayName'];
const CELL_FILTER_BLACKLIST: RunTypes.Key[] = [
  {section: 'run', name: 'github'},
  {section: 'run', name: 'runInfo.gpu'},
  {section: 'run', name: 'runInfo.gpuCount'},
];

type AllRunSelectorProps = RunSelectorInnerProps & RunSelectorPropsHook;

interface RunSelectorInnerState {
  tableMessage?: JSX.Element;
  creatingSweep: boolean;
  currentPage: number;
  sortPopupOpen: boolean;
}

class RunSelectorInnerComp extends React.Component<
  AllRunSelectorProps,
  RunSelectorInnerState
> {
  state: RunSelectorInnerState = {
    tableMessage: undefined,
    creatingSweep: false,
    currentPage: 1,
    sortPopupOpen: false,
  };

  didPropsOrStateChange = makeDidPropsOrStateChange({
    name: 'RunSelector',
    deep: [
      'runSelectorQuery',
      'sort',
      'runSetFull',
      'updateGroupRunTags',
      'runSet',
    ],
    debug: false,
    verbose: true,
  });

  tableRef = React.createRef<WBTableComp>();

  rows = memoize((rowData: RunTypes.Run[], selectedRunName?: string) => {
    return rowData.map(r => {
      return {
        ...r,
        selectedRunName,
        runsetInfo: {
          id: this.props.runSetFull.id,
          name: this.props.runSetFull.name,
        },
        __address__: TableCols.makeRowAddressComponent(
          r.name,
          this.props.runSetFull.id
        ),
      };
    });
  });

  lastRowCount = 0;

  renderCell = ({
    addFilter,
    benchmarkMeta,
    column,
    disabledFeatures,
    disableRunLinks,
    displayBenchmark,
    entityName,
    firstActiveRunName,
    isGroup,
    isGroupExpanded,
    isHoveringRow,
    loadingChildren,
    projectName,
    recursionDepth,
    row,
    toggleExpandedRow,
  }: RenderCellOpts) => {
    const isGroupRun = isGroup && row.group === row.name;
    const columnKey = column.key;
    const value = Run.getValue(row, columnKey);
    if (CELL_FILTER_BLACKLIST.some(f => Run.keysEqual(columnKey, f))) {
      return <WBTableCellDefault columnKey={columnKey} cellValue={value} />;
    }

    const onAddTag = (tagName: string) => {
      if (isGroup) {
        this.addGroupTag(row, tagName);
      } else {
        this.addTag(row, tagName);
      }
    };

    const onDeleteTag = (tagName: string) => {
      if (isGroup) {
        this.deleteGroupTag(row, tagName);
      } else {
        this.deleteTag(row, tagName);
      }
    };

    if (columnKey.section === 'tags') {
      return this.renderTagsCell({
        disabledFeatures,
        entityName,
        isGroup,
        isGroupRun,
        isHoveringRow,
        onAddFilter: addFilter,
        onAddTag,
        onDeleteTag,
        projectName,
        row,
      });
    }

    if (Run.keysEqual(columnKey, {section: 'run', name: 'state'})) {
      return (
        <WBTableCellDefault
          columnKey={columnKey}
          cellValue={row.state}
          addFilter={addFilter}>
          <RunState value={row.state} />
        </WBTableCellDefault>
      );
    }

    if (Run.keysEqual(columnKey, {section: 'run', name: 'createdAt'})) {
      return (
        <WBTableCellCreatedAt createdAt={row.createdAt} addFilter={addFilter} />
      );
    }

    if (Run.keysEqual(columnKey, {section: 'run', name: 'heartbeatAt'})) {
      return (
        <WBTableCellDefault
          columnKey={columnKey}
          cellValue={Run.formatTimestamp(row.heartbeatAt)}
          addFilter={addFilter}
        />
      );
    }

    if (Run.keysEqual(columnKey, {section: 'run', name: 'updatedAt'})) {
      return (
        <WBTableCellDefault
          columnKey={columnKey}
          cellValue={Run.formatTimestamp(row.updatedAt)}
          addFilter={addFilter}
        />
      );
    }

    if (Run.keysEqual(columnKey, {section: 'run', name: 'duration'})) {
      return (
        <TimeDisplay
          timestamp={new Date(row.createdAt)}
          format="month_round"
          now={new Date(row.heartbeatAt)}
        />
      );
    }

    if (Run.keysEqual(columnKey, {section: 'run', name: 'notes'})) {
      const isNotesReadOnly =
        (isGroup && !isGroupRun) || disabledFeatures['edit-run-notes'];
      return (
        <EditableLabel
          data-test="add-notes-input"
          placeholder={isNotesReadOnly ? '-' : 'Add notes...'}
          readOnly={isNotesReadOnly}
          onBlur={(newNotes: string) => {
            isGroupRun
              ? this.saveRunGroupNotes(row, newNotes)
              : this.saveRunNotes(row, newNotes);
          }}
          serverText={row.notes}
          showPopup
        />
      );
    }

    if (
      Run.keysEqual(columnKey, {
        section: 'run',
        name: 'sweep',
      })
    ) {
      return (
        <>
          {row.sweep ? (
            disableRunLinks ? (
              <WBTableCellDefault
                columnKey={columnKey}
                cellValue={getSweepDisplayName(row.sweep)}
                addFilter={addFilter}
              />
            ) : (
              <Link
                to={urls.sweep({
                  entityName,
                  projectName,
                  sweepName: row.sweep.name,
                })}>
                {getSweepDisplayName(row.sweep)}
              </Link>
            )
          ) : (
            '-'
          )}
        </>
      );
    }

    if (
      Run.keysEqual(columnKey, {
        section: 'run',
        name: 'displayName',
      })
    ) {
      return this.renderRowHeader({
        benchmarkMeta,
        disableRunLinks,
        displayBenchmark,
        isHoveringRow,
        entityName,
        isFirstActiveRun: row.name === firstActiveRunName,
        isGroup,
        isGroupExpanded,
        isLoadingChildren: loadingChildren,
        projectName,
        recursionDepth,
        row,
        toggleExpandedRow,
      });
    }
    if (value != null) {
      return (
        <WBTableCellDefault
          columnKey={columnKey}
          cellValue={value}
          addFilter={addFilter}
        />
      );
    }

    return '-';
  };

  renderRowHeader = ({
    benchmarkMeta,
    disableRunLinks,
    displayBenchmark,
    isHoveringRow,
    entityName,
    isFirstActiveRun,
    isGroup,
    isGroupExpanded,
    isLoadingChildren,
    projectName,
    recursionDepth,
    row,
    toggleExpandedRow,
  }: RenderRowHeaderOpts) => {
    return (
      <RunSelectorRowHeader
        benchmarkMeta={benchmarkMeta}
        customRunColorsRef={this.props.customRunColorsRef}
        customRunNamesRef={this.props.customRunNamesRef}
        disabledFeatures={this.props.disabledFeatures}
        disableRunLink={disableRunLinks}
        displayBenchmark={displayBenchmark}
        displayTempCheckbox={isHoveringRow}
        entityName={entityName}
        groupSelectionsRef={this.props.runSet.groupSelectionsRef}
        isFirstActiveRun={isFirstActiveRun}
        isGroup={isGroup}
        isGroupExpanded={isGroupExpanded}
        isInReport={this.props.isInReport}
        isSingleMode={this.props.isSingleMode}
        isTableCollapsed={this.props.isTableCollapsed}
        loadingChildren={isLoadingChildren}
        onSelectRunName={this.props.onSelectRunName}
        onTempCheckboxClick={this.clearTableMessage}
        projectName={projectName}
        recursionDepth={recursionDepth}
        row={row}
        runSetRef={this.props.runSetRef}
        selectedRunName={this.props.selectedRunName}
        semanticLegendRef={this.props.semanticLegendRef}
        showArtifactCounts={this.props.showArtifactCounts}
        showLogCounts={this.props.showLogCounts}
        tempSelectionsRef={this.props.tempSelectionsRef}
        onGroupButtonClick={() => {
          if (isGroup) {
            return toggleExpandedRow && toggleExpandedRow(row.__address__);
          }
        }}
        saveRunName={this.saveRunName}
        stopRun={id => this.props.stopRun({variables: {id}}).then(this.reload)}
        setLaunchDrawerRow={this.props.setLaunchDrawerRow}
        isLaunchActive={this.props.isLaunchActive}
        truncationType={this.props.truncationType}
      />
    );
  };

  renderTagsCell = ({
    disabledFeatures,
    entityName,
    isGroup,
    isGroupRun,
    isHoveringRow,
    onAddFilter,
    onAddTag,
    onDeleteTag,
    projectName,
    row,
  }: RenderTagsCellOpts) => {
    if (isGroup && !isGroupRun) {
      return '';
    }

    const isTagsReadOnly = disabledFeatures.tag;
    const isFiltersReadOnly = disabledFeatures.filter;

    return (
      <Tailwind>
        <div data-test="icon-wrap" className="flex items-center gap-4">
          {row.tags.map((tag, index) => (
            <WorkspaceTag
              key={index}
              tag={tag}
              style={{
                cursor: isTagsReadOnly ? 'unset' : 'pointer',
              }}
              onClick={() => {
                if (!isFiltersReadOnly && onAddFilter) {
                  onAddFilter({section: 'tags', name: tag.name}, '=', true);
                }
              }}
              onRemoveClick={
                isTagsReadOnly
                  ? undefined
                  : (e: React.SyntheticEvent) => {
                      e.stopPropagation();
                      onDeleteTag(tag.name);
                    }
              }
            />
          ))}
          {!isTagsReadOnly && (
            <WBPopupTrigger
              popupContent={({close}) => (
                <Tailwind>
                  <RunTagInfiniteDropdown
                    entityName={entityName}
                    projectName={projectName}
                    isMultiSelect
                    toggleSelection={async tag => {
                      for (const t of row.tags) {
                        if (t.name === tag) {
                          onDeleteTag(tag);
                          return;
                        }
                      }
                      onAddTag(tag);
                      close();
                    }}
                    selections={row.tags.map(t => t.name)}
                  />
                </Tailwind>
              )}>
              {({anchorRef, setOpen, open}) => {
                return (
                  <Button
                    className={classNames('tag-add-button-icon-only', {
                      hidden: !isHoveringRow && !open,
                    })}
                    data-test="tag-add-button"
                    onClick={() => setOpen(!open)}
                    ref={anchorRef}
                    variant="ghost"
                    size="small"
                    icon="add-new"
                    aria-label="Add a new tag"
                    tooltip="Add a new tag"
                  />
                );
              }}
            </WBPopupTrigger>
          )}
        </div>
      </Tailwind>
    );
  };

  renderHeader = ({
    displayedRows,
    tableSettings,
    totalRowCount,
  }: RenderHeaderOpts) => {
    return (
      <RunsTableHeader
        isSingleMode={this.props.isSingleMode}
        totalRowCount={totalRowCount}
        displayedRows={displayedRows}
        runSetRef={this.props.runSetRef}
        tempSelectionsRef={this.props.tempSelectionsRef}
        customRunNamesRef={this.props.customRunNamesRef}
        showTempSelection={!this.props.isExpandable || !!this.props.isExpanded}
        onlyShowSelected={tableSettings.onlyShowSelected}
        disabledFeatures={this.props.disabledFeatures}
        isInReport={this.props.isInReport}
        onAllItemsTempSelected={() => {
          // Show the "select all runs in project?" prompt.
          // TODO(views): This could maybe be factored out somehow.
          if (totalRowCount > displayedRows.length) {
            const isGrouped = !_.isEmpty(this.props.groupSelections.grouping);
            this.setState({
              tableMessage: (
                <>
                  All <strong>{displayedRows.length}</strong>
                  {isGrouped ? ' groups ' : ' runs '} on this page are selected.{' '}
                  <span
                    className="fake-link"
                    onClick={() => {
                      this.props.tempSelectAll();
                      this.clearTableMessage();
                    }}>
                    Select all
                    {isGrouped ? ' groups ' : ' runs '} in this project
                  </span>
                </>
              ),
            });
          }
        }}
        onAllItemsTempUnselected={this.clearTableMessage}
        onVisibilitySelectNone={this.props.selectNone}
        onVisibilitySelectAll={this.props.selectAll}
        onSetOnlyShowSelected={newSetting => {
          this.props.setTableSettings({
            ...tableSettings,
            onlyShowSelected: newSetting,
          });
          this.reload();
        }}
      />
    );
  };

  allColumns = memoize(
    (
      entityName: string,
      projectName: string,
      defaultKeys: RunTypes.Key[],
      filteredKeys: RunTypes.Key[],
      tableSettings: TableSettings,
      totalRowCount: number,
      firstActiveRunName: string | undefined,
      benchmarkMeta: BenchmarkProject | undefined,
      displayBenchmark: boolean | undefined
    ) => {
      const {disableRunLinks} = this.props;
      const filteredKeyStrings = filteredKeys.map(k => Run.keyToString(k));
      const orderKeys = tableSettings.columnOrder
        .filter(k => !_.includes(filteredKeyStrings, k))
        .map(k => Run.keyFromString(k)!);

      const unionedKeys = TableCols.unionKeys(defaultKeys, [
        ...filteredKeys,
        ...orderKeys,
      ]);

      return unionedKeys.map(key => {
        const renderCell: WBTableColumn['renderCell'] = (
          column,
          row: WBTableRowWithRunSetInfo,
          {
            addFilter,
            toggleExpandedRow,
            recursionDepth,
            isGroup,
            isGroupExpanded,
            loadingChildren,
            isHoveringRow,
          }
        ) => {
          return this.renderCell({
            addFilter,
            benchmarkMeta,
            column,
            disableRunLinks: Boolean(disableRunLinks),
            displayBenchmark: Boolean(displayBenchmark),
            disabledFeatures: this.props.disabledFeatures || {},
            entityName,
            firstActiveRunName,
            isGroup: Boolean(isGroup),
            isGroupExpanded: Boolean(isGroupExpanded),
            isHoveringRow: Boolean(isHoveringRow),
            loadingChildren: Boolean(loadingChildren),
            projectName,
            recursionDepth,
            row,
            toggleExpandedRow,
          });
        };

        let renderHeader = undefined;
        if (
          Run.keysEqual(key, {
            section: 'run',
            name: 'displayName',
          })
        ) {
          renderHeader = (displayedRows: RunTypes.Run[]) =>
            this.renderHeader({
              displayedRows,
              tableSettings,
              totalRowCount,
            });
        }

        return TableCols.keyToColumn(key, {
          renderHeader,
          renderCell,
        });
      });
    },
    (newArgs, oldArgs) => {
      if (
        !Array.isArray(newArgs) ||
        !Array.isArray(oldArgs) ||
        newArgs.length !== oldArgs.length
      ) {
        return false;
      }
      const [
        entityName,
        projectName,
        defaultKeys,
        filteredKeys,
        tableSettings,
        totalRowCount,
        benchmarkMeta,
        displayBenchmark,
      ] = newArgs;
      const [
        oldEntityName,
        oldProjectName,
        oldDefaultKeys,
        oldFilteredKeys,
        oldTableSettings,
        oldTotalRowCount,
        oldBenchmarkMeta,
        oldDisplayBenchmark,
      ] = oldArgs;
      return (
        entityName === oldEntityName &&
        projectName === oldProjectName &&
        _.isEqual(defaultKeys, oldDefaultKeys) &&
        filteredKeys === oldFilteredKeys &&
        tableSettings === oldTableSettings &&
        totalRowCount === oldTotalRowCount &&
        _.isEqual(benchmarkMeta, oldBenchmarkMeta) &&
        displayBenchmark === oldDisplayBenchmark
      );
    }
  );

  getExtraTableActionsLeft = memoize(
    (
      entityName,
      hasEntityStorageEnabled,
      projectName,
      enableSetProject,
      enableFreezeRunset,
      groupSelections,
      username,
      tempSelectNone,
      tags,
      disabledFeatures,
      tableSettings: TableSettings
    ) => (
      <>
        {enableSetProject && (
          <ProjectPicker
            entityName={entityName}
            projectName={projectName}
            setProject={this.props.setProject}
            projectId={this.props.runSet.project?.id}
          />
        )}
        {!disabledFeatures?.search && (
          <div className={'runs-search-bar-wrapper'}>
            <RunsSearchBar
              isGroupSearch={Query.isGroupedByGroup(groupSelections.grouping)}
              isInReport={this.props.isInReport}
              search={{
                query: this.props.runSet.search.query,
                isRegex: this.props.runSet.search.isRegex ?? true,
              }}
              setSearch={(query, isRegex) => {
                if (this.tableRef.current) {
                  this.tableRef.current.resetPagination();
                }
                this.props.setSearch(query, isRegex);
              }}
              searchHistory={this.props.runSet.searchHistory}
              onAddToHistory={this.props.addToHistory}
            />
          </div>
        )}
        {this.props.isLaunchActive && (
          <JobDrawerLocked
            open={this.props.launchDrawerJobRun != null}
            closeDrawer={this.props.closeLaunchConfigModal}
            entityName={entityName}
            projectName={projectName}
            jobId={this.props.launchDrawerJobRun?.jobId ?? ''}
            jobVersion={this.props.launchDrawerJobRun?.jobVersion ?? ''}
            defaultRunToClone={this.props.launchDrawerJobRun?.runName}
          />
        )}
        <S.TableActionButtonsWrapper>
          {!disabledFeatures?.filter && (
            <RunsFreezeAndFilter
              enableFreezeRunset={enableFreezeRunset}
              filtersRef={this.props.runSet.filtersRef}
              entityName={entityName}
              isInReport={this.props.isInReport}
              projectName={projectName}
              username={username}
              compact={!!this.props.isExpandable && !this.props.isExpanded}
              onChange={() => {
                if (this.tableRef.current != null) {
                  this.tableRef.current.resetPagination();
                }
              }}
              tableSettings={tableSettings}
            />
          )}
          {!disabledFeatures?.group && (
            <RunsGroupTableAction
              entityName={entityName}
              projectName={projectName}
              groupSelectionsRef={this.props.runSet.groupSelectionsRef}
              recommendedGrouping={this.props.recommendedGrouping}
              compact={!!this.props.isExpandable && !this.props.isExpanded}
              isInReport={this.props.isInReport}
              onGroupingChanged={() => {
                this.tableRef.current?.resetPagination();
                tempSelectNone();
              }}
              tableSettings={tableSettings}
            />
          )}
          {!disabledFeatures?.sort && (
            <RunsSortTableAction
              entityName={entityName}
              projectName={projectName}
              sortRef={this.props.runSet.sortRef}
              compact={!!this.props.isExpandable && !this.props.isExpanded}
              isInReport={this.props.isInReport}
              open={this.state.sortPopupOpen}
              onOpen={() => this.setState({sortPopupOpen: true})}
              onClose={() => this.setState({sortPopupOpen: false})}
              onSortChanged={() =>
                this.tableRef.current && this.tableRef.current.resetPagination()
              }
              tableSettings={tableSettings}
            />
          )}
          {this.props.isExpandable && !this.props.isExpanded ? (
            <></>
          ) : (
            <>
              {/* BATCH TAGGING, MOVING, DELETING */}
              {!disabledFeatures?.tag && (
                <BulkTagEditor
                  // TODO(views): Here and below, this is a bad way to pass these counts through as it
                  // won't always update.
                  selectedCount={this.props.tempSelectedCount}
                  isInReport={this.props.isInReport}
                  tags={tags}
                  addTag={(tag: string) => this.bulkUpdateTag('add', tag)}
                  removeTag={(tag: string) => this.bulkUpdateTag('remove', tag)}
                />
              )}
              {!disabledFeatures?.move && this.props.tempSelectedCount ? (
                <MoveRunDrawerBulkButton
                  entityName={this.props.entityName}
                  groupTempSelection={this.props.groupTempSelection}
                  isInReport={Boolean(this.props.isInReport)}
                  onMovedRuns={() => {
                    this.props.tempSelectNone();
                    this.reload();
                  }}
                  projectName={this.props.projectName}
                  groupSelectionsRef={this.props.runSet.groupSelectionsRef}
                />
              ) : null}

              {!disabledFeatures?.move && (
                <RunMover
                  entityName={entityName}
                  // prevent users from trying to move runs to another entity if the entity is using external storage
                  // because this operation does not move the run files
                  requireSameEntity={hasEntityStorageEnabled}
                  requireSameEntityReason={
                    "Can't move runs between entities when using external storage"
                  }
                  isInReport={this.props.isInReport}
                  projectName={projectName}
                  selectedCount={this.props.tempSelectedCount}
                  refetch={() => this.reload()}
                  onMove={(entity, proj) => this.moveRuns(entity, proj)}
                />
              )}
              {!disabledFeatures?.delete && (
                <DeleteRunsButton
                  selectedCount={this.props.tempSelectedCount}
                  isInReport={this.props.isInReport}
                  onDelete={(deleteArtifacts: boolean) =>
                    this.deleteRuns(deleteArtifacts)
                  }
                  outputArtifactsCount={this.props.tempOutputArtifactsCount}
                  activeRunsCount={this.props.tempSelectedActiveCount}
                />
              )}
              {!disabledFeatures?.['create-sweep'] && (
                <CreateSweepButton onClick={this.createSweepWithPriorRuns} />
              )}
            </>
          )}
        </S.TableActionButtonsWrapper>
      </>
    )
  );

  getExtraTableActionsRight = memoize(
    (allQueryVariables, allColumns, tableSettings, currentPage) => {
      return (
        <>
          {!this.props.disabledFeatures?.export && (
            <RunsDownloadMenu
              pageQuery={allQueryVariables}
              tableSettings={tableSettings}
            />
          )}
          {!this.props.disabledFeatures?.['auto-cols'] && (
            <RunsAutoCols
              query={allQueryVariables}
              tableSettings={tableSettings}
              isInReport={this.props.isInReport}
              setTableSettings={this.props.setTableSettings}
              currentPage={currentPage}
              columns={allColumns}
            />
          )}
        </>
      );
    }
  );

  saveRunNotes = _.debounce((run: RunTypes.Run, newNotes: string) => {
    const currentNotes = run.notes;
    if (newNotes !== currentNotes) {
      // Sending undefined values for displayName or notes clears them, so
      // we always need to send both.
      this.props.updateRun(run.id, run.name, {
        displayName: run.displayName,
        notes: newNotes,
      });
    }
  }, 500);

  saveRunGroupNotes = _.debounce((run: RunTypes.Run, newNotes: string) => {
    const {group: name, tags} = run;
    const {entityName, projectName} = this.props;
    this.props.updateGroupRun({
      variables: {
        projectName,
        entityName,
        name,
        tags: tags.map(t => t.name),
        notes: newNotes,
      },
    });
  }, 500);

  setSearch = (query: string, isRegex: boolean) => {
    if (this.tableRef.current) {
      this.tableRef.current.resetPagination();
    }
    this.props.setSearch(query, isRegex);
  };

  updateSort = (updateFn: (sort: QueryTypes.Sort) => void) => {
    const {sort, setSort} = this.props;
    const newSort = produce(sort, updateFn);
    setSort(newSort);
  };

  clearTableMessage = (callback?: () => void) => {
    this.setState({tableMessage: undefined}, () => {
      if (callback) {
        callback();
      }
    });
  };

  shouldComponentUpdate(
    nextProps: RunSelectorInnerProps,
    nextState: RunSelectorInnerState
  ): boolean {
    return this.didPropsOrStateChange(
      [this.props, nextProps],
      [this.state, nextState]
    );
  }

  saveRunName = (run: RunTypes.Run, newName: string) => {
    const currentName = run.displayName || run.name;
    if (newName !== currentName) {
      // Sending undefined values for displayName or notes clears them, so
      // we always need to send both.
      this.props.updateRun(run.id, run.name, {
        displayName: newName,
        notes: run.notes,
      });
      window.analytics?.track('Run name edited');
    }
  };

  addTag = (run: RunTypes.Run, newTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (!currentTagNames.includes(newTagName)) {
      this.props.updateRunTags(run, [...currentTagNames, newTagName]);
    }
  };

  addGroupTag = (run: RunTypes.Run, newTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (currentTagNames.includes(newTagName)) {
      return;
    }

    this.props.updateGroupRunTags(run, [...currentTagNames, newTagName]);
  };

  deleteTag = (run: RunTypes.Run, removeTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (_.includes(currentTagNames, removeTagName)) {
      this.props.updateRunTags(
        run,
        currentTagNames.filter(t => t !== removeTagName)
      );
    }
  };

  deleteGroupTag = (run: RunTypes.Run, removeTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (!currentTagNames.includes(removeTagName)) {
      return;
    }
    this.props.updateGroupRunTags(
      run,
      currentTagNames.filter(t => t !== removeTagName)
    );
  };

  bulkUpdateTag(actionType: 'add' | 'remove', tagName: string): void {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();
    this.props
      .bulkUpdateRunTags({
        entityName,
        projectName,
        filters: JSON.stringify(queryFilters),
        [actionType === 'add' ? 'addTags' : 'removeTags']: [tagName],
      })
      .then(() => {
        // Refreshes the <BulkTagEditor> menu
        if (
          !this.props.bulkEditorProjectTagsQuery.loading &&
          this.props.bulkEditorProjectTagsQuery.refetch
        ) {
          this.props.bulkEditorProjectTagsQuery.refetch();
        }
      });
  }

  moveRuns(
    destinationEntityName: string,
    destinationProjectName: string
  ): Promise<string> {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();

    return this.props
      .moveRuns({
        variables: {
          sourceEntityName: entityName,
          sourceProjectName: projectName,
          destinationEntityName,
          destinationProjectName,
          filters: JSON.stringify(queryFilters),
        },
      })
      .then(response => {
        if (response.data == null || response.data.moveRuns == null) {
          throw new Error('Unexpected response');
        }
        return response.data.moveRuns.task.id;
      });
  }

  deleteRuns(deleteArtifacts: boolean): void {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();

    this.props
      .deleteRuns({
        variables: {
          entityName,
          projectName,
          filters: JSON.stringify(queryFilters),
          deleteArtifacts,
        },
      })
      .then(resp => {
        if (resp.data?.deleteRuns?.errors) {
          toast(resp.data.deleteRuns.errors, {type: 'error', autoClose: 2500});
        } else {
          toast('Runs deleted', {type: 'success', autoClose: 2500});
        }
        this.reload();
      })
      .catch(err => {
        let errorMessage = 'Unable to delete runs.';
        if (err?.networkError?.response?.status === 403) {
          errorMessage = 'Unable to delete runs. Invalid permissions.';
        }

        toast(errorMessage, {type: 'error', autoClose: 2500});
      });
  }

  createSweepWithPriorRuns = () => this.setState({creatingSweep: true});

  combinedFilter = () => {
    const {filters, mergeFilters, runSet, customRunNames} = this.props;
    const groupTempSelection = this.props.groupTempSelection;

    return combinedFilter(
      filters,
      groupTempSelection,
      undefined,
      mergeFilters,
      runSet.search,
      customRunNames
    );
  };

  reload = () => {
    if (
      !this.props.runSelectorQuery.loading &&
      this.props.runSelectorQuery.refetch
    ) {
      this.props.runSelectorQuery.refetch();
    }
    // Refreshes the <BulkTagEditor> menu
    if (
      !this.props.bulkEditorProjectTagsQuery.loading &&
      this.props.bulkEditorProjectTagsQuery.refetch
    ) {
      this.props.bulkEditorProjectTagsQuery.refetch();
    }
  };

  columnEditor = (
    config: TableSettings,
    setTableSettings: (config: TableSettings) => void
  ) => {
    const {entityName, projectName} = this.props;
    return (
      <RunsColumnEditor
        entityName={entityName}
        projectName={projectName}
        config={config}
        fixedColumns={FIXED_COLUMNS}
        update={setTableSettings}
      />
    );
  };

  onSetCurrentPage = (currentPage: number) => {
    this.setState({currentPage});
  };

  openSortPopup = () => this.setState({sortPopupOpen: true});

  componentDidUpdate() {
    const {entityName, projectName, runSetFull, tempSelections} = this.props;
    const {creatingSweep} = this.state;

    if (creatingSweep) {
      window.analytics?.track('Create Sweep Clicked', {
        entityName,
        projectName,
        location: 'runs table',
      });
      this.props.dispatch(
        setCreateSweepArgs({
          runSet: runSetFull,
          tempSelections,
        })
      );
    }
  }

  render() {
    const {
      benchmarkMeta,
      disabledFeatures,
      defaultKeys,
      displayBenchmark,
      enableFreezeRunset,
      enableSetProject,
      entityName,
      filteredKeys,
      groupSelections,
      isLoadingRuns,
      isLoadingRunsPolled,
      loadMoreRuns,
      projectName,
      onHeightChange,
      runs,
      selectedRunName,
      setGrouping,
      tableSettings,
      tempSelectNone,
      topLevelQueryVariables,
      totalRuns,
    } = this.props;
    const {tableMessage, creatingSweep, currentPage} = this.state;

    if (creatingSweep) {
      return (
        <Redirect to={`/${entityName}/${projectName}/create-sweep`} push />
      );
    }

    const runSelectorQuery = this.props.runSelectorQuery;
    const username = this.props.viewer ? this.props.viewer.username : '';

    const project = runSelectorQuery.initialLoading
      ? undefined
      : runSelectorQuery?.project;
    const hasEntityStorageEnabled =
      project?.entity.settings?.storageBucketInfo != null;

    let tags: TagState[] = [];
    const bulkTagsProject = this.props.bulkEditorProjectTagsQuery.initialLoading
      ? undefined
      : this.props.bulkEditorProjectTagsQuery.data?.project;
    if (bulkTagsProject) {
      const counts: {[key: string]: number} = {};
      (bulkTagsProject.tagCounts || []).forEach(t => {
        counts[t.name] = t.count;
      });
      tags = (bulkTagsProject.tags || []).map(tag => ({
        name: tag.name,
        colorIndex: tag.colorIndex,
        count: counts[tag.name] || 0,
      }));
    }

    const rows = this.rows(runs, selectedRunName);
    const firstActiveRunName = runs.find(r => r.state === 'running')?.name;
    const totalRowCount = totalRuns;
    const allColumns = this.allColumns(
      entityName,
      projectName,
      defaultKeys,
      filteredKeys,
      tableSettings,
      totalRowCount,
      firstActiveRunName,
      benchmarkMeta,
      displayBenchmark
    );

    if (rows.length !== this.lastRowCount) {
      this.lastRowCount = rows.length;
      onHeightChange?.();
    }

    const shouldShowTableLoading =
      (runSelectorQuery.initialLoading || isLoadingRuns) &&
      !isLoadingRunsPolled;

    const tableProps: WBTableProps = {
      className:
        'run-selector' +
        (this.props.className ? ` ${this.props.className}` : '') +
        (this.props.isExpandable ? ` expandable` : ''),
      clearTableMessage: this.clearTableMessage,
      columns: allColumns,
      disabledFeatures: this.props.disabledFeatures,
      expandable: this.props.isExpandable,
      expanded: this.props.isExpanded,
      expandedRowAddresses: groupSelections.expandedRowAddresses,
      filters: this.props.filters,
      fixedColumns: FIXED_COLUMNS,
      grouping: groupSelections.grouping,
      isInReport: this.props.isInReport,
      isSingleMode: this.props.isSingleMode,
      loading: shouldShowTableLoading,
      onSetExpanded: this.props.onSetExpanded,
      rows,
      setFilters: this.props.setFilters,
      setGrouping,
      setTableSettings: this.props.setTableSettings,
      showArtifactCounts: this.props.showArtifactCounts,
      showLogCounts: this.props.showLogCounts,
      SortIndicatorComponent: makeRunsSortIndicator(this.props.runSet.sortRef),
      tableLoadMore: loadMoreRuns,
      tableMessage,
      tableSettings,
      title: this.props.title,
      toggleExpandedRow: this.props.toggleExpandedRow,
      totalRowCount,
      updateSort: this.updateSort,
      updateTruncationType: this.props.setTruncationType,
      truncationType: this.props.truncationType,
      extraTableActionsLeft: this.getExtraTableActionsLeft(
        entityName,
        hasEntityStorageEnabled,
        projectName,
        enableSetProject,
        enableFreezeRunset,
        groupSelections,
        username,
        tempSelectNone,
        tags,
        disabledFeatures,
        tableSettings
      ),
      extraTableActionsRight: this.getExtraTableActionsRight(
        topLevelQueryVariables,
        allColumns,
        tableSettings,
        currentPage
      ),
      columnEditor: this.columnEditor,
      onSetCurrentPage: this.onSetCurrentPage,
      openSortPopup: this.openSortPopup,
      topLevelQueryVariables,
      sort: this.props.sort,
    };

    return <WBTable tableRef={this.tableRef} {...tableProps} />;
  }
}

export const RunSelectorInner = (props: RunSelectorInnerProps) => {
  const selectedProps = useRunSelectorProps(props);
  return <RunSelectorInnerComp {...props} {...selectedProps} />;
};
